import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  // Sidebar Icons
  LayoutDashboard,
  Users,
  GraduationCap,
  Briefcase,
  CreditCard,
  FileText,
  BookOpen,
  Calendar,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  TrendingUp,
  TrendingDown,
  UserPlus,
  Clock,
  DollarSign,
  Upload,
  FileCheck,
  ChevronDown,
  Bell,
  Search,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  Target,
  CalendarCheck,
  
  // Priority Card Icons
  Award,
  
  // Status Icons
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import ConsultationsModule from '../../components/Admin/ConsultationsModule';
import StudyPipelineModule from '../../components/Admin/StudyPipelineModule';
import WorkPipelineModule from '../../components/Admin/WorkPipelineModule';
import PaymentsModule from '../../components/Admin/PaymentsModule';
import RolesModule from '../../components/Admin/RolesModule';
import adminApiService from '../../services/adminApiService';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    priorityStats: {},
    recentActivity: [],
    pipelineOverview: {}
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await adminApiService.dashboard.getStats();
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Set fallback data on error
      setDashboardData({
        priorityStats: {
          newConsultationsToday: 0,
          consultationsPendingReview: 0,
          candidatesAwaitingPayment: 0,
          documentsPendingReview: 0,
          sopFormsSubmitted: 0,
          interviewsToSchedule: 0,
          offersReceived: 0
        },
        recentActivity: [],
        pipelineOverview: {
          study: { total: 0, active: 0, completed: 0 },
          work: { total: 0, active: 0, completed: 0 }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Operational overview'
    },
    {
      id: 'consultations',
      label: 'Consultations',
      icon: Users,
      description: 'Manage consultation leads'
    },
    {
      id: 'study-pipeline',
      label: 'Study Pipeline',
      icon: GraduationCap,
      description: 'Study abroad candidates'
    },
    {
      id: 'work-pipeline',
      label: 'Work Pipeline',
      icon: Briefcase,
      description: 'Work abroad candidates'
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: CreditCard,
      description: 'Payment tracking'
    },
    {
      id: 'roles',
      label: 'Available Roles',
      icon: Briefcase,
      description: 'Manage job roles'
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: FileText,
      description: 'Document review'
    },
    {
      id: 'sop-applications',
      label: 'SOP & Applications',
      icon: BookOpen,
      description: 'Study applications'
    },
    {
      id: 'interviews',
      label: 'Interviews',
      icon: Calendar,
      description: 'Interview scheduling'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: MessageSquare,
      description: 'Communication hub'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      description: 'Analytics & insights'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'System configuration'
    }
  ];

  const priorityCards = [
    {
      title: 'New Consultations Today',
      value: dashboardData.priorityStats.newConsultationsToday,
      icon: UserPlus,
      color: 'bg-blue-500',
      trend: 'up',
      change: '+3 from yesterday'
    },
    {
      title: 'Consultations Pending Review',
      value: dashboardData.priorityStats.consultationsPendingReview,
      icon: Clock,
      color: 'bg-yellow-500',
      trend: 'down',
      change: '-2 from yesterday'
    },
    {
      title: 'Candidates Awaiting Payment',
      value: dashboardData.priorityStats.candidatesAwaitingPayment,
      icon: DollarSign,
      color: 'bg-green-500',
      trend: 'up',
      change: '+1 from yesterday'
    },
    {
      title: 'Documents Pending Review',
      value: dashboardData.priorityStats.documentsPendingReview,
      icon: Upload,
      color: 'bg-purple-500',
      trend: 'up',
      change: '+5 from yesterday'
    },
    {
      title: 'SOP Forms Submitted',
      value: dashboardData.priorityStats.sopFormsSubmitted,
      icon: FileCheck,
      color: 'bg-indigo-500',
      trend: 'up',
      change: '+2 from yesterday'
    },
    {
      title: 'Interviews to Schedule',
      value: dashboardData.priorityStats.interviewsToSchedule,
      icon: CalendarCheck,
      color: 'bg-orange-500',
      trend: 'down',
      change: '-1 from yesterday'
    },
    {
      title: 'Offers Received',
      value: dashboardData.priorityStats.offersReceived,
      icon: Award,
      color: 'bg-pink-500',
      trend: 'up',
      change: '+4 from yesterday'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'normal': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            {/* Priority Cards */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Operational Priorities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {priorityCards.map((card, index) => {
                  const Icon = card.icon;
                  const TrendIcon = card.trend === 'up' ? TrendingUp : TrendingDown;
                  
                  return (
                    <motion.div
                      key={card.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-lg ${card.color} bg-opacity-10`}>
                          <Icon className={`${card.color.replace('bg-', 'text-')}`} size={24} />
                        </div>
                        <div className={`flex items-center gap-1 text-sm ${
                          card.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          <TrendIcon size={16} />
                          <span>{card.change}</span>
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{card.value}</h3>
                      <p className="text-sm text-gray-600">{card.title}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Payment Statistics */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Payment Overview</h3>
                <button
                  onClick={() => setActiveSection('payments')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View All Payments →
                </button>
              </div>
              
              {/* Revenue Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="text-green-600" size={20} />
                    <span className="text-xs text-green-600 font-medium">Total Revenue</span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    ${dashboardData.paymentStats?.totalRevenue?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-green-600 mt-1">All time revenue</div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="text-blue-600" size={20} />
                    <span className="text-xs text-blue-600 font-medium">This Month</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    ${dashboardData.paymentStats?.monthRevenue?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">Monthly revenue</div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Calendar className="text-purple-600" size={20} />
                    <span className="text-xs text-purple-600 font-medium">Today</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    ${dashboardData.paymentStats?.todayRevenue?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-purple-600 mt-1">Today's revenue</div>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="text-orange-600" size={20} />
                    <span className="text-xs text-orange-600 font-medium">Conversion Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-900">
                    {dashboardData.paymentStats?.paymentConversionRate || '0'}%
                  </div>
                  <div className="text-xs text-orange-600 mt-1">Payment conversion</div>
                </div>
              </div>

              {/* Program Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Study Abroad */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="text-blue-600" size={20} />
                    <h4 className="font-semibold text-blue-900">Study Abroad</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-blue-900">
                        ${dashboardData.paymentStats?.studyRevenue?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-blue-600">Total Revenue</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-900">
                        {dashboardData.paymentStats?.studyPaidStudents || '0'}
                      </div>
                      <div className="text-sm text-blue-600">Paid Students</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-blue-900">
                        ${dashboardData.paymentStats?.studyPaymentAmount || '149'}
                      </div>
                      <div className="text-sm text-blue-600">Per Student</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-blue-900">
                        {dashboardData.paymentStats?.studyConversionRate || '0'}%
                      </div>
                      <div className="text-sm text-blue-600">Conversion</div>
                    </div>
                  </div>
                </div>

                {/* Work Abroad */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="text-green-600" size={20} />
                    <h4 className="font-semibold text-green-900">Work Abroad</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-green-900">
                        ${dashboardData.paymentStats?.workRevenue?.toLocaleString() || '0'}
                      </div>
                      <div className="text-sm text-green-600">Total Revenue</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-900">
                        {dashboardData.paymentStats?.workPaidStudents || '0'}
                      </div>
                      <div className="text-sm text-green-600">Paid Students</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-green-900">
                        ${dashboardData.paymentStats?.workPaymentAmount || '199'}
                      </div>
                      <div className="text-sm text-green-600">Per Student</div>
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-green-900">
                        {dashboardData.paymentStats?.workConversionRate || '0'}%
                      </div>
                      <div className="text-sm text-green-600">Conversion</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Status Overview */}
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-sm font-medium text-gray-700">Paid Students:</span>
                      <span className="text-sm font-bold text-green-600">
                        {dashboardData.paymentStats?.totalPaidStudents || '0'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="text-red-600" size={16} />
                      <span className="text-sm font-medium text-gray-700">Awaiting Payment:</span>
                      <span className="text-sm font-bold text-red-600">
                        {dashboardData.paymentStats?.totalUnpaidStudents || '0'}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    Total: {(dashboardData.paymentStats?.totalPaidStudents || 0) + (dashboardData.paymentStats?.totalUnpaidStudents || 0)} students
                  </div>
                </div>
              </div>
            </div>

            {/* Pipeline Overview & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pipeline Overview */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Overview</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="text-blue-600" size={20} />
                      <span className="font-medium text-blue-900">Study Abroad</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-900">{dashboardData.pipelineOverview.study.total}</div>
                      <div className="text-sm text-blue-600">{dashboardData.pipelineOverview.study.active} active</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Briefcase className="text-green-600" size={20} />
                      <span className="font-medium text-green-900">Work Abroad</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-900">{dashboardData.pipelineOverview.work.total}</div>
                      <div className="text-sm text-green-600">{dashboardData.pipelineOverview.work.active} active</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {dashboardData.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className={`p-2 rounded-full ${getPriorityColor(activity.priority)}`}>
                        <AlertCircle size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-center transition-colors">
                  <UserPlus className="text-blue-600 mx-auto mb-2" size={24} />
                  <span className="text-sm font-medium text-blue-900">Add Candidate</span>
                </button>
                <button className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-center transition-colors">
                  <DollarSign className="text-green-600 mx-auto mb-2" size={24} />
                  <span className="text-sm font-medium text-green-900">Mark Payment</span>
                </button>
                <button className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-center transition-colors">
                  <Users className="text-purple-600 mx-auto mb-2" size={24} />
                  <span className="text-sm font-medium text-purple-900">Assign Advisor</span>
                </button>
                <button className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg text-center transition-colors">
                  <MessageSquare className="text-orange-600 mx-auto mb-2" size={24} />
                  <span className="text-sm font-medium text-orange-900">Send Update</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'consultations':
        return <ConsultationsModule />;
      
      case 'study-pipeline':
        return <StudyPipelineModule />;
      
      case 'work-pipeline':
        return <WorkPipelineModule />;
      
      case 'payments':
        return <PaymentsModule />;
      
      case 'roles':
        return <RolesModule />;
      
      default:
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {menuItems.find(item => item.id === activeSection)?.label}
            </h2>
            <p className="text-gray-600">
              This section is under development. Check back soon!
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex flex-col">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <img src={logo} alt="Notapaths" className="h-8" />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <motion.aside
          initial={false}
          animate={{
            x: sidebarOpen ? 0 : -320,
            opacity: sidebarOpen ? 1 : 0
          }}
          className={`fixed lg:static lg:h-full inset-y-0 left-0 z-40 w-80 bg-white border-r border-gray-200 overflow-y-auto flex flex-col ${
            sidebarOpen ? 'block' : 'hidden lg:block'
          }`}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-200">
            <img src={logo} alt="Notapaths" className="h-10 mb-2" />
            <p className="text-sm text-gray-600 mt-1">Admin Operations Center</p>
            <div className="mt-3 p-3 bg-[#202356] text-white rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="text-white" size={16} />
                <span className="text-sm font-medium">Admin Access</span>
              </div>
              <p className="text-xs opacity-90">
                {user?.firstName} {user?.lastName}
              </p>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="p-4 flex-1">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        setActiveSection(item.id);
                        if (window.innerWidth < 1024) {
                          setSidebarOpen(false);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-[#202356] text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon size={20} />
                      <div className="flex-1 text-left">
                        <div className="font-medium text-sm">{item.label}</div>
                        <p className="text-xs opacity-75 mt-0.5">{item.description}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </motion.aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6">
            {renderSection()}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
